import {
  Directive,
  ElementRef,
  inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2,
  SimpleChange,
  type SimpleChanges
} from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';

interface ComponentChanges extends SimpleChanges {
  resizable?: SimpleChange;
  min?: SimpleChange;
  max?: SimpleChange;
}

@UntilDestroy()
@Directive({
  selector: '[resizeColumn]',
  host: {
    '(window:resize)': 'onWindowResize($event)'
  }
})
export class ResizeColumnDirective implements OnInit, OnChanges, OnDestroy {
  private readonly el = inject(ElementRef);
  private readonly ngZone = inject(NgZone);
  private readonly renderer = inject(Renderer2);

  @Input('resizeColumn') resizable: boolean;

  @Input('resizeColumnMax') max = '30%';
  @Input('resizeColumnMin') min = '16.66666667%';

  @Input() reverse: boolean;
  @Input() resizeThreshold = 3;

  private column: HTMLElement;
  private grid: HTMLElement;
  private resizer: HTMLSpanElement;

  private pressed: boolean;
  private startX: number;
  private startWidth: number;

  private removeListeners: (() => void)[] = [];

  private readonly holderClass = 'mbs-resize-holder';

  onWindowResize(event: Event): void {
    if (!(event.target instanceof Window)) return;

    this.updateClasses();
    this.updateStyles();
  }

  ngOnDestroy(): void {
    this.removeListeners.forEach((unsub) => unsub());
  }

  ngOnChanges(changes: ComponentChanges): void {
    if (changes.resizable && !changes.resizable.firstChange) {
      this.ngOnDestroy();
      this.init();
    }

    if (this.resizable && (changes.min || changes.max)) {
      this.updateStyles();
    }
  }

  ngOnInit(): void {
    this.resizable && this.init();
  }

  private init(): void {
    this.initInstances();

    this.insertResizer();
    this.initStyles();
    this.initClasses();

    this.initListeners();
  }

  private initInstances(): void {
    this.column = this.column || this.el.nativeElement;
    this.grid = this.grid || this.renderer.parentNode(this.column);

    this.resizer = this.resizer || this.renderer.createElement('span');
  }

  private insertResizer(): void {
    this.renderer.addClass(this.resizer, this.holderClass);
    this.renderer.appendChild(this.column, this.resizer);
  }

  private initStyles(): void {
    this.renderer.setStyle(this.column, 'width', this.min);
    this.renderer.setStyle(this.column, 'min-width', this.min);
    this.renderer.setStyle(this.column, 'max-width', this.max);
  }

  private initClasses(): void {
    this.reverse && this.renderer.addClass(this.resizer, '--reverse');
  }

  private initListeners(): void {
    this.ngZone.runOutsideAngular(() => {
      const unsubs = [
        this.renderer.listen(this.resizer, 'mousedown', (event: MouseEvent) => {
          this.ngZone.run(() => this.onMouseDown(event));
        }),
        this.renderer.listen(this.grid, 'mousemove', (event: MouseEvent) => {
          this.onMouseMove(event);
        }),
        this.renderer.listen('document', 'mouseup', () => {
          this.ngZone.run(() => this.onMouseUp());
        })
      ];

      this.removeListeners.push(...unsubs);
    });
  }

  private updateClasses(): void {
    if (!this.resizer) return;

    this.resizable ? this.renderer.addClass(this.resizer, this.holderClass) : this.renderer.removeClass(this.resizer, this.holderClass);
  }

  private updateStyles(): void {
    if (!this.column) return;

    this.resizable ? this.renderer.setStyle(this.column, 'width', this.min) : this.renderer.removeStyle(this.column, 'width');
    this.resizable ? this.renderer.setStyle(this.column, 'min-width', this.min) : this.renderer.removeStyle(this.column, 'min-width');
    this.resizable ? this.renderer.setStyle(this.column, 'max-width', this.max) : this.renderer.removeStyle(this.column, 'max-width');
  }

  private onMouseDown(e: MouseEvent): void {
    this.pressed = true;
    this.startX = e.pageX;
    this.startWidth = this.column.offsetWidth;
  }

  private onMouseMove(e: MouseEvent): void {
    if (!this.pressed || !e.buttons || !this.hasExceededThreshold(e)) return;

    e.preventDefault();

    let width = this.reverse ? this.startWidth + (this.startX - e.pageX) : this.startWidth + (e.pageX - this.startX);

    const minWidth = this.getPxValue(this.min);
    const maxWidth = this.getPxValue(this.max);

    width = Math.max(minWidth, Math.min(maxWidth, width));

    this.renderer.addClass(this.grid, 'resizing');
    this.renderer.setStyle(this.column, 'width', `${width}px`);
  }

  private hasExceededThreshold(event: MouseEvent): boolean {
    return Math.abs(event.pageX - this.startX) >= this.resizeThreshold;
  }

  private getPxValue(value: string): number {
    return value.includes('%') ? (parseFloat(value) / 100) * this.grid.offsetWidth : parseFloat(value);
  }

  private onMouseUp(): void {
    if (!this.pressed) return;

    this.pressed = false;
    this.renderer.removeClass(this.grid, 'resizing');
  }
}
